import { MainService } from "../../services/optimizer-service/optimizer.service";
import { CONSTANTS } from "../Constants";

declare var google: {
    maps: {
        OverlayView: {
            new();
        };
        Size: {
            new(width: number, height: number);
        };
        Point: {
            new(x: number, y: number);
        };
    }
};

export interface DriverInfoTrip {
    id: string;
    state?: string;
    stopCount?: number;
    completedStops?: number;
    dropOffCount?: number;
    passengerCount?: number;
    startTime?: any;
    endTime?: any;
}

export class CustomInfoWindow extends google.maps.OverlayView {
    private static activeWindow: CustomInfoWindow | null = null;

    position: any;
    content: string;
    div: HTMLDivElement;
    private visible = false;

    constructor(map: any, position: any, content: string) {
        super();
        this.position = position;
        this.content = content;

        this.div = document.createElement('div');
        this.div.innerHTML = this.content;
        this.div.style.position = 'absolute';
        this.div.style.display = 'none';
        this.div.className = 'custom-info-window';
        this.div.addEventListener('click', (e: MouseEvent) => {
            const target = (e.target as HTMLElement).closest('.ciw-close-btn');
            if (target) {
                e.preventDefault();
                e.stopPropagation();
                this.close();
            }
        });
    }

    static getWindowContent(driver: any): string {
        const d = Object.assign({}, driver);
        const payload = MainService.getADMINpayload();
        if (payload && payload.type === 'BEARER') {
            d.pickupType = 'BEARER';
        }
        const trips = CustomInfoWindow.resolveAssignedTrips(d);
        const firstName = d.firstname != null ? String(d.firstname) : '';
        const lastName = d.lastname != null ? String(d.lastname) : '';
        const fullName = `${firstName} ${lastName}`.trim() || 'Driver';
        const initials = CustomInfoWindow.getInitials(firstName, lastName);
        const isOnline = String(d.status || '').toLowerCase() === 'online';
        const assignedCount = trips.length;
        const startedCount = trips.filter(t => CustomInfoWindow.isTripStarted(t)).length;
        const stopsCount = trips.reduce((sum, t) => sum + CustomInfoWindow.getTripStopCount(t), 0);
        const startedPercent = assignedCount > 0 ? Math.round((startedCount / assignedCount) * 100) : 0;

        const accentColor = d.mapColor ? String(d.mapColor) : '';
        const avatarStyle = accentColor
            ? ` style="background:${CustomInfoWindow.tintColor(accentColor, 0.82)};color:${accentColor};border:2px solid ${accentColor}"`
            : '';
        const tripsHtml = assignedCount > 0
            ? trips.map(t => CustomInfoWindow.renderTripRow(t, accentColor)).join('')
            : `<div class="ciw-no-trips">No assigned trips</div>`;
        return `
        <div class="ciw-card">
            <div class="ciw-header">
                <div class="ciw-avatar"${avatarStyle}>${CustomInfoWindow.escapeHtml(initials)}</div>
                <div class="ciw-header-text">
                    <div class="ciw-name">${CustomInfoWindow.escapeHtml(fullName)}</div>
                    <div class="ciw-status">
                        <span class="ciw-status-dot ${isOnline ? 'ciw-status-dot--online' : 'ciw-status-dot--offline'}"></span>
                        <span class="ciw-status-label">${CustomInfoWindow.escapeHtml(d.status || 'Offline')}</span>
                    </div>
                </div>
                <button type="button" class="ciw-close-btn" aria-label="Close">&times;</button>
            </div>
            <div class="ciw-stats">
                <div class="ciw-stat">
                    <span class="ciw-stat-label">Assigned</span>
                    <span class="ciw-stat-value">${assignedCount}</span>
                </div>
                <div class="ciw-stat">
                    <span class="ciw-stat-label">Started</span>
                    <span class="ciw-stat-value">${startedCount}</span>
                </div>
                <div class="ciw-stat">
                    <span class="ciw-stat-label">Stops</span>
                    <span class="ciw-stat-value">${stopsCount}</span>
                </div>
            </div>
            <div class="ciw-trips-section">
                ${tripsHtml}
            </div>
            <div class="ciw-footer">
                <span class="ciw-footer-text">${startedCount} of ${assignedCount} trips started</span>
                <span class="ciw-footer-percent">${startedPercent}%</span>
            </div>
            <div class="ciw-footer-bar">
                <div class="ciw-footer-bar-fill" style="width: ${startedPercent}%${accentColor ? `;background:${accentColor}` : ''}"></div>
            </div>
        </div>`;
    }

    static resolveAssignedTrips(driver: any): DriverInfoTrip[] {
        const raw = driver.assignedTrips || driver.assigned_trips || driver.trips;
        let trips: DriverInfoTrip[] = [];
        if (Array.isArray(raw) && raw.length > 0) {
            trips = raw.map(t => CustomInfoWindow.normalizeTrip(t)).filter(t => t.id);
        } else {
            const activeTrip = driver.activeTrip != null ? String(driver.activeTrip) : '';
            if (activeTrip && activeTrip !== 'FREE') {
                const passengerCount = Number(driver.passengerCount) || 0;
                const dropOffCount = Number(driver.dropOffCount) || 0;
                const startTime = driver.startTime || driver.tripStartTime || driver.trip_start_time;
                const endTime = driver.endTime || driver.tripEndTime || driver.trip_end_time;
                const trip: DriverInfoTrip = {
                    id: activeTrip,
                    state: driver.tripState || driver.trip_state || driver.activeTripState || driver.active_trip_state
                        || driver.tripStatus || driver.trip_status,
                    passengerCount,
                    dropOffCount,
                    completedStops: dropOffCount,
                    stopCount: passengerCount || dropOffCount,
                    startTime,
                    endTime
                };
                trip.state = CustomInfoWindow.resolveTripState(trip);
                trips = [trip];
            }
        }
        return CustomInfoWindow.sortTripsForDisplay(trips);
    }

    /** Started trips first, then assigned; stable order by trip id within each group. */
    static sortTripsForDisplay(trips: DriverInfoTrip[]): DriverInfoTrip[] {
        return trips.slice().sort((a, b) => {
            const aStarted = CustomInfoWindow.isTripStarted(a);
            const bStarted = CustomInfoWindow.isTripStarted(b);
            if (aStarted !== bStarted) {
                return aStarted ? -1 : 1;
            }
            return String(a.id).localeCompare(String(b.id));
        });
    }

    private static normalizeTrip(t: any): DriverInfoTrip {
        const id = t.id != null ? String(t.id) : (t.tripId != null ? String(t.tripId) : '');
        const passengerCount = Number(t.passengerCount != null ? t.passengerCount : t.passenger_count) || 0;
        const dropOffCount = Number(t.dropOffCount != null ? t.dropOffCount : t.drop_off_count) || 0;
        const stopCount = Number(t.stopCount != null ? t.stopCount : t.stop_count) || passengerCount || dropOffCount;
        const completedStops = Number(t.completedStops != null ? t.completedStops : t.completed_stops) || dropOffCount;
        const trip: DriverInfoTrip = {
            id,
            state: t.state || t.status || t.trip_state || t.tripState,
            passengerCount,
            dropOffCount,
            stopCount,
            completedStops,
            startTime: t.startTime != null ? t.startTime : t.start_time,
            endTime: t.endTime != null ? t.endTime : t.end_time
        };
        trip.state = CustomInfoWindow.resolveTripState(trip);
        return trip;
    }

    private static renderTripRow(trip: DriverInfoTrip, accentColor?: string): string {
        const started = CustomInfoWindow.isTripStarted(trip);
        const tripId = CustomInfoWindow.escapeHtml(trip.id);
        const url = `${window.location.origin}/admin-portal/trip-details/${encodeURIComponent(trip.id)}`;
        const stopTotal = CustomInfoWindow.getTripStopCount(trip);
        const completed = Math.min(
            CustomInfoWindow.getTripCompletedStops(trip),
            stopTotal > 0 ? stopTotal : CustomInfoWindow.getTripCompletedStops(trip)
        );
        let progress = stopTotal > 0 ? Math.round((completed / stopTotal) * 100) : 0;
        if (started) {
            progress = Math.max(progress, 50);
        }
        const statusClass = started ? 'ciw-trip-badge--started' : 'ciw-trip-badge--assigned';
        const statusLabel = started ? 'Started' : 'Assigned';
        const pinClass = started ? 'ciw-trip-pin--active' : 'ciw-trip-pin';
        const accent = accentColor ? String(accentColor) : '';
        const pinStyle = accent ? ` style="color:${accent}"` : '';
        const progressStyle = accent ? ` style="width:${progress}%;background:${accent}"` : ` style="width:${progress}%"`;

        return `
        <a class="ciw-trip-row" href="${url}" target="_blank" rel="noopener noreferrer">
            <span class="${pinClass}"${pinStyle}><i class="fa fa-map-marker" aria-hidden="true"></i></span>
            <span class="ciw-trip-main">
                <span class="ciw-trip-title">Trip ${tripId}</span>
                <span class="ciw-trip-progress-track">
                    <span class="ciw-trip-progress-fill"${progressStyle}></span>
                </span>
            </span>
            <span class="ciw-trip-badge ${statusClass}">${statusLabel}</span>
        </a>`;
    }

    private static isTripStarted(trip: DriverInfoTrip): boolean {
        return CustomInfoWindow.resolveTripState(trip) === CONSTANTS.TRIP_STARTED;
    }

    /** Normalize trip state for display badges and counts. */
    static resolveTripState(trip: DriverInfoTrip): string {
        const normalized = CustomInfoWindow.normalizeStateValue(trip.state);
        if (CustomInfoWindow.isStartedState(normalized)) {
            return CONSTANTS.TRIP_STARTED;
        }
        if (normalized === CONSTANTS.TRIP_COMPLETED || normalized === CONSTANTS.TRIP_ENDED) {
            return normalized;
        }
        if (CustomInfoWindow.hasStartedTimestamp(trip.startTime, trip.endTime)) {
            return CONSTANTS.TRIP_STARTED;
        }
        if ((Number(trip.dropOffCount) || 0) > 0 || (Number(trip.completedStops) || 0) > 0) {
            return CONSTANTS.TRIP_STARTED;
        }
        return normalized || CONSTANTS.TRIP_CREATED;
    }

    private static normalizeStateValue(value: any): string {
        return String(value == null ? '' : value).trim().toUpperCase().replace(/\s+/g, '_');
    }

    private static isStartedState(state: string): boolean {
        return state === CONSTANTS.TRIP_STARTED
            || state === 'IN_PROGRESS'
            || state === 'ACTIVE'
            || state === 'ONGOING';
    }

    private static hasStartedTimestamp(startTime: any, endTime?: any): boolean {
        if (startTime == null || startTime === '') {
            return false;
        }
        if (endTime != null && endTime !== '') {
            return false;
        }
        return true;
    }

    private static getTripStopCount(trip: DriverInfoTrip): number {
        const stops = Number(trip.stopCount);
        if (!isNaN(stops) && stops > 0) {
            return stops;
        }
        const passengers = Number(trip.passengerCount);
        return !isNaN(passengers) && passengers > 0 ? passengers : 0;
    }

    private static getTripCompletedStops(trip: DriverInfoTrip): number {
        const completed = Number(trip.completedStops != null ? trip.completedStops : trip.dropOffCount);
        return !isNaN(completed) && completed > 0 ? completed : 0;
    }

    private static getInitials(first: string, last: string): string {
        const a = (first || '').trim().charAt(0);
        const b = (last || '').trim().charAt(0);
        const initials = `${a}${b}`.toUpperCase();
        return initials || '?';
    }

    /** Lighten a hex color for avatar backgrounds (amount 0–1). */
    private static tintColor(hex: string, amount: number): string {
        const normalized = String(hex || '').replace('#', '');
        if (normalized.length !== 6) {
            return '#f3f4f6';
        }
        const r = parseInt(normalized.substring(0, 2), 16);
        const g = parseInt(normalized.substring(2, 4), 16);
        const b = parseInt(normalized.substring(4, 6), 16);
        const mix = (channel: number) => Math.round(channel + (255 - channel) * amount);
        const toHex = (n: number) => {
            const h = n.toString(16);
            return h.length === 1 ? `0${h}` : h;
        };
        return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
    }

    /** Map marker icon: pin SVG with the driver's initials in the head. */
    static getDriverMarkerIcon(driver: any): { url: string; scaledSize: any; anchor: any } {
        const firstName = driver && driver.firstname != null ? String(driver.firstname) : '';
        const lastName = driver && driver.lastname != null ? String(driver.lastname) : '';
        const initials = CustomInfoWindow.getInitials(firstName, lastName);
        const isOnline = String(driver && driver.status || '').toLowerCase() === 'online';
        const mapColor = driver && driver.mapColor ? String(driver.mapColor) : '';
        let pinColor: string;
        if (mapColor) {
            pinColor = mapColor;
        } else if (isOnline) {
            pinColor = '#0488DB';
        } else {
            pinColor = '#6b7280';
        }
        const safeInitials = CustomInfoWindow.escapeHtml(initials);
        const safePinColor = String(pinColor).replace(/[<>"'&]/g, '');

        // Pin artwork is authored at 586×616; marker size controls on-map legibility (~11px text at 56px wide).
        const pinViewW = 586;
        const pinViewH = 616;
        const headCx = 293;
        const headCy = 222;
        const headR = 130;
        const textClipR = headR - 8;
        const markerWidth = 56;
        const markerHeight = Math.round(markerWidth * (pinViewH / pinViewW));
        const initialsLen = safeInitials.length || 1;
        const fontSize = initialsLen > 2 ? 92 : initialsLen === 2 ? 108 : 124;

        const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${pinViewW}" height="${pinViewH}" viewBox="0 0 ${pinViewW} ${pinViewH}">
  <defs>
    <clipPath id="driverInitialsClip">
      <circle cx="${headCx}" cy="${headCy}" r="${textClipR}"/>
    </clipPath>
  </defs>
  <path d="M293,5 C168,5 65,105 65,225 C65,320 145,415 278,595 Q293,618 308,595 C436,415 521,320 521,225 C521,105 418,5 293,5 Z" fill="${safePinColor}"/>
  <circle cx="${headCx}" cy="${headCy}" r="${headR}" fill="#FFFFFF"/>
  <text x="${headCx}" y="${headCy}" clip-path="url(#driverInitialsClip)" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-weight="700" font-size="${fontSize}" fill="${safePinColor}" dy="0.35em">${safeInitials}</text>
</svg>`;
        return {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
            scaledSize: new google.maps.Size(markerWidth, markerHeight),
            anchor: new google.maps.Point(markerWidth / 2, markerHeight)
        };
    }

    private static escapeHtml(value: any): string {
        return String(value == null ? '' : value).replace(/[&<>"']/g, ch => {
            switch (ch) {
                case '&': return '&amp;';
                case '<': return '&lt;';
                case '>': return '&gt;';
                case '"': return '&quot;';
                case "'": return '&#39;';
                default: return ch;
            }
        });
    }

    onAdd() {
        const panes = this.getPanes();
        panes.floatPane.appendChild(this.div);
    }

    draw() {
        const overlayProjection = this.getProjection();
        const position = overlayProjection.fromLatLngToDivPixel(this.position);

        const offsetX = -140;
        const cardHeight = this.div.offsetHeight || 320;
        const top = position.y - cardHeight - 24;

        this.div.style.left = (position.x + offsetX) + 'px';
        this.div.style.top = top + 'px';
    }

    setPosition(position) {
        this.position = position;
        this.draw();
    }

    updateContent(content) {
        this.content = content;
        this.div.innerHTML = this.content;
        this.draw();
    }

    open() {
        if (CustomInfoWindow.activeWindow && CustomInfoWindow.activeWindow !== this) {
            CustomInfoWindow.activeWindow.close();
        }
        this.visible = true;
        this.div.style.display = 'block';
        CustomInfoWindow.activeWindow = this;
        requestAnimationFrame(() => this.draw());
    }

    close() {
        this.visible = false;
        this.div.style.display = 'none';
        if (CustomInfoWindow.activeWindow === this) {
            CustomInfoWindow.activeWindow = null;
        }
    }

    toggle() {
        if (this.visible) {
            this.close();
        } else {
            this.open();
        }
    }

    onRemove() {
        if (CustomInfoWindow.activeWindow === this) {
            CustomInfoWindow.activeWindow = null;
        }
        if (this.div && this.div.parentNode) {
            this.div.parentNode.removeChild(this.div);
            this.div = null!;
        }
    }
}
